import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { useWindows } from '../../contexts/WindowContext';
import { getGradientClasses } from '../../utils/helpers';
import { GeometricPattern } from '../../utils/patterns';
import { typography } from '../../utils/theme';
import { PlayIcon, CodeIcon, LayoutIcon, SparklesIcon, BrainIcon, MessageCircleIcon, ArrowDownIcon, UserIcon, FolderIcon, ChevronDownIcon } from 'lucide-react';
export function DesktopWidgets() {
  // Destructuring van context hooks
  const {
    getThemeClasses,
    theme,
    reduceMotion
  } = useTheme();
  const {
    openWindow
  } = useWindows();
  const themeClasses = getThemeClasses();
  const gradients = getGradientClasses(theme); // Niet gebruikt, maar behouden
  // Creative showcase items - Gewrapped in useMemo voor optimalisatie
  const showcaseItems = useMemo(() => [{
    type: 'video',
    title: 'AI Character Animation',
    subtitle: 'Kling AI + Flux Pro',
    thumbnail: 'https://images.pexels.com/photos/8728380/pexels-photo-8728380.jpeg?auto=compress&cs=tinysrgb&w=600',
    videoUrl: 'https://player.vimeo.com/progressive_redirect/playback/855551359/rendition/720p/file.mp4?loc=external&signature=f33dba70e7d6d3d58a1ef34b6f569594418b276e8e7bd077e72e182d634aec6e',
    color: 'from-rose-500 to-pink-600',
    size: 'large',
    rotation: -2,
    app: 'portfolio'
  }, {
    type: 'ui',
    title: 'BULKRR App Design',
    subtitle: 'AI-driven Shopping',
    image: 'https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=600',
    color: 'from-emerald-500 to-teal-600',
    size: 'medium',
    rotation: 3,
    app: 'portfolio'
  }, {
    type: 'prototype',
    title: 'Interactive Dashboard',
    subtitle: 'React + AI Integration',
    image: 'https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg?auto=compress&cs=tinysrgb&w=600',
    color: 'from-blue-500 to-indigo-600',
    size: 'medium',
    rotation: -1,
    app: 'portfolio'
  }, {
    type: 'ui',
    title: 'Camping App UX',
    subtitle: 'Group Experience Design',
    image: 'https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=600',
    color: 'from-sky-500 to-blue-600',
    size: 'small',
    rotation: 2,
    app: 'portfolio'
  }, {
    type: 'code',
    title: 'AI Prompt Engineering',
    subtitle: 'Character System',
    color: 'from-purple-500 to-violet-600',
    size: 'small',
    rotation: -3,
    app: 'expertise',
    codeSnippet: `// AI Character Prompt
const prompt = \`Create a character with:
- Personality: Curious explorer
- Visual: Cyberpunk aesthetic
- Emotion: Wonder and excitement
\``
  }, {
    type: 'video',
    title: 'Motion Design',
    subtitle: 'Micro-interactions',
    thumbnail: 'https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=600',
    color: 'from-amber-500 to-orange-600',
    size: 'medium',
    rotation: 1,
    app: 'process'
  }], []);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  // CSS for 3D animation - Gewrapped in useMemo voor optimalisatie
  const css = useMemo(() => `
    .card-container {
      perspective: 1200px;
      transform-style: preserve-3d;
    }
    .card-3d {
      transform-style: preserve-3d;
      transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
      will-change: transform;
    }
    .card-3d-animation-1 {
      animation: float1 8s ease-in-out infinite;
    }
    .card-3d-animation-2 {
      animation: float2 10s ease-in-out infinite;
    }
    .card-3d-animation-3 {
      animation: float3 12s ease-in-out infinite;
    }
    @keyframes float1 {
      0%, 100% { transform: rotateX(2deg) rotateY(3deg) translateZ(0); }
      25% { transform: rotateX(-2deg) rotateY(1deg) translateZ(15px); }
      50% { transform: rotateX(1deg) rotateY(-2deg) translateZ(25px); }
      75% { transform: rotateX(-1deg) rotateY(-1deg) translateZ(15px); }
    }
    @keyframes float2 {
      0%, 100% { transform: rotateX(-2deg) rotateY(-3deg) translateZ(0); }
      33% { transform: rotateX(1deg) rotateY(-1deg) translateZ(20px); }
      66% { transform: rotateX(-1deg) rotateY(2deg) translateZ(10px); }
    }
    @keyframes float3 {
      0%, 100% { transform: rotateX(1deg) rotateY(1deg) translateZ(5px); }
      50% { transform: rotateX(-1deg) rotateY(-2deg) translateZ(20px); }
    }
    .card-hover {
      transform: rotateX(0deg) rotateY(0deg) translateZ(40px) scale(1.05) !important;
      box-shadow: 0 30px 60px -15px rgba(0, 0, 0, 0.4) !important;
      animation: none !important;
    }
    .pin-3d {
      transform: translateZ(50px);
    }
    @keyframes pulse-glow {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.8; }
    }
    .pulse-glow {
      animation: pulse-glow 2s ease-in-out infinite;
    }
  `, []);
  // Geoptimaliseerde constanten voor de loop (gebruik useMemo)
  const {
    positions,
    sizes
  } = useMemo(() => ({
    // Responsive positions that stay in viewport
    positions: [{
      top: '5%',
      left: '8%'
    }, {
      top: '10%',
      left: '38%'
    }, {
      top: '5%',
      left: '68%'
    }, {
      top: '50%',
      left: '12%'
    }, {
      top: '55%',
      left: '72%'
    }, {
      top: '52%',
      left: '42%'
    }],
    // Responsive sizes based on viewport
    sizes: {
      large: 'w-64 sm:w-80 md:w-96 h-40 sm:h-52 md:h-64',
      medium: 'w-56 sm:w-72 md:w-80 h-36 sm:h-44 md:h-52',
      small: 'w-48 sm:w-56 md:w-64 h-32 sm:h-36 md:h-40'
    }
  }), []);
  // Gemeenschappelijke motion transition voor showcase items
  const showcaseTransition = {
    type: 'spring',
    damping: 20
  };
  // Map voor iconen (voor cleaner rendering in de loop)
  const typeIcons = {
    video: PlayIcon,
    ui: LayoutIcon,
    prototype: SparklesIcon,
    code: CodeIcon
  };
  return <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-714">
      <style data-id="element-715">{`{!reduceMotion ? css : ''}`}</style>
      {/* Main container - nu met flex voor betere afstand */}
      {/* BELANGRIJK: pointer-events-none op de flex container blijft staan. */}
      <div className="w-full h-full flex flex-col justify-between items-center px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8 pointer-events-none" data-id="element-716">
        {/* Hero Section - Aangepast voor betere schaalvergroting */}
        <motion.div initial={{
        opacity: 0,
        y: -30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1,
        duration: 0.6
      }}
      // BELANGRIJK: pointer-events-auto op Hero Section om knoppen klikbaar te maken.
      className="text-center mb-4 sm:mb-6 pointer-events-auto z-10 relative max-w-4xl" data-id="element-717">
          {/* Badge */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2
        }} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${themeClasses.card} border ${themeClasses.border} backdrop-blur-xl mb-4 sm:mb-6 shadow-lg`} data-id="element-718">
            <SparklesIcon className="w-4 h-4 text-purple-500" data-id="element-719" />
            <span className={`text-sm font-medium ${themeClasses.text}`} data-id="element-720">
              UX Designer & AI Developer
            </span>
          </motion.div>
          {/* Main Title - responsive text sizing */}
          <h1 className={`${typography.h1} mb-3 sm:mb-4 tracking-tight ${themeClasses.text} text-3xl sm:text-4xl md:text-5xl`} data-id="element-721">
            Van Complexe Vraag naar
            <br data-id="element-722" />
            <span className="bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 bg-clip-text text-transparent" data-id="element-723">
              Elegante Oplossing
            </span>
          </h1>
          {/* Subtitle - reduced max-width for better fit */}
          <p className={`${typography.body} ${themeClasses.text} opacity-70 max-w-xl mx-auto mb-6 leading-relaxed text-sm sm:text-base`} data-id="element-724">
            Ik combineer design, AI en development om complexe uitdagingen te
            vertalen naar intuïtieve digitale ervaringen die gebruikers
            begrijpen en waarderen.
          </p>
          {/* CTA Buttons - improved responsive layout */}
          <div className="flex gap-2 sm:gap-4 justify-center mb-4 flex-wrap" data-id="element-725">
            <button onClick={() => openWindow('about', 'About Mick')} className={`group px-3 sm:px-6 py-2 sm:py-3 rounded-xl ${themeClasses.card} border-2 ${themeClasses.border} ${themeClasses.text} font-semibold transition-all hover:scale-105 hover:shadow-xl flex items-center gap-2 shadow-lg backdrop-blur-xl text-sm sm:text-base`} data-id="element-726">
              <UserIcon className="w-4 h-4" data-id="element-727" />
              Lees meer over mij
            </button>
            <button onClick={() => openWindow('portfolio', 'Portfolio')} className="group px-3 sm:px-6 py-2 sm:py-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white font-semibold transition-all hover:scale-105 hover:shadow-2xl shadow-lg flex items-center gap-2 text-sm sm:text-base" data-id="element-728">
              <FolderIcon className="w-4 h-4" data-id="element-729" />
              Bekijk Portfolio
            </button>
            <button onClick={() => openWindow('contact', 'Contact')} className={`group px-3 sm:px-6 py-2 sm:py-3 rounded-xl ${themeClasses.card} border-2 ${themeClasses.border} ${themeClasses.text} font-semibold transition-all hover:scale-105 hover:shadow-xl flex items-center gap-2 shadow-lg backdrop-blur-xl text-sm sm:text-base`} data-id="element-730">
              <MessageCircleIcon className="w-4 h-4" data-id="element-731" />
              Start Gesprek
            </button>
          </div>
          {/* Scroll indicator - only show on larger screens */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1
        }} className={`hidden sm:flex flex-col items-center gap-2 ${themeClasses.text} opacity-50`} data-id="element-732">
            <motion.div animate={{
            y: [0, 8, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-733">
              <ArrowDownIcon className="w-4 h-4" data-id="element-734" />
            </motion.div>
          </motion.div>
        </motion.div>
        {/* Featured Work Section - Aangepast */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="w-full max-w-6xl mb-2 sm:mb-4 pointer-events-auto" data-id="element-735"></motion.div>
        {/* 3D Pinboard Showcase - aangepast voor betere pasvorm en schaal */}
        {/* BELANGRIJK: pointer-events-auto op Showcase container om kaarten klikbaar te maken. */}
        <div className="relative w-full max-w-7xl pointer-events-auto card-container flex-grow" style={{
        height: 'min(50vh, 450px)'
      }} data-id="element-736">
          {showcaseItems.map((item, index) => {
          // Icon component
          const ItemIcon = typeIcons[item.type];
          // Choose a different animation pattern based on index
          const animationClass = !reduceMotion ? `card-3d-animation-${index % 3 + 1}` : '';
          return <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            ...showcaseTransition,
            delay: 0.5 + index * 0.1
          }} onHoverStart={() => setHoveredItem(index)} onHoverEnd={() => setHoveredItem(null)} onClick={() => openWindow(item.app, item.title)} className={`absolute ${sizes[item.size]} cursor-pointer`} style={{
            top: positions[index]?.top || '50%',
            left: positions[index]?.left || '50%',
            zIndex: hoveredItem === index ? 50 : 10 + index,
            transformOrigin: 'center center',
            // Ensure cards don't go offscreen
            maxWidth: 'calc(100vw - 2rem)',
            maxHeight: 'calc(50vh - 2rem)'
          }} data-id="element-737">
                <div className={`relative w-full h-full card-3d ${animationClass} ${hoveredItem === index ? 'card-hover' : ''}`} style={{
              transform: `rotate(${item.rotation}deg)`
            }} data-id="element-738">
                  <div className={`relative w-full h-full rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl`} style={{
                boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.2)'
              }} data-id="element-739">
                    {/* Background gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-20`} data-id="element-740" />
                    {/* Content based on type */}
                    {item.type === 'video' && <>
                        <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" data-id="element-741" />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center" data-id="element-742">
                          <div className={`w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center`} data-id="element-743">
                            <PlayIcon className="w-6 sm:w-8 h-6 sm:h-8 text-white ml-1" data-id="element-744" />
                          </div>
                        </div>
                      </>}
                    {item.type === 'ui' && item.image && <>
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-745" />
                        <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-30 mix-blend-multiply`} data-id="element-746" />
                      </>}
                    {item.type === 'prototype' && item.image && <>
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-747" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-748" />
                      </>}
                    {item.type === 'code' && item.codeSnippet && <div className="w-full h-full p-4 sm:p-6 flex flex-col justify-center" data-id="element-749">
                        <div className={`w-10 sm:w-12 h-10 sm:h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-3 sm:mb-4`} data-id="element-750">
                          <CodeIcon className="w-5 sm:w-6 h-5 sm:h-6 text-white" data-id="element-751" />
                        </div>
                        <pre className={`text-xs ${themeClasses.text} opacity-70 font-mono overflow-hidden line-clamp-4`} data-id="element-752">
                          {item.codeSnippet}
                        </pre>
                      </div>}
                    {/* Label overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 bg-gradient-to-t from-black/80 to-transparent" data-id="element-753">
                      <div className="flex items-center gap-2 mb-0.5 sm:mb-1" data-id="element-754">
                        {/* Gebruik van de ItemIcon-constante */}
                        {ItemIcon && <ItemIcon className="w-3 sm:w-4 h-3 sm:h-4 text-white" data-id="element-755" />}
                        <h3 className="text-white font-bold text-xs sm:text-sm" data-id="element-756">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-white/70 text-[10px] sm:text-xs" data-id="element-757">
                        {item.subtitle}
                      </p>
                    </div>
                    {/* Hover indicator */}
                    {hoveredItem === index && <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="absolute inset-0 border-4 border-white/20 rounded-2xl pointer-events-none" data-id="element-758" />}
                  </div>
                  {/* Pin effect */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-5 sm:w-6 h-5 sm:h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg pin-3d" data-id="element-759" />
                </div>
              </motion.div>;
        })}
        </div>
        {/* Menu indicator - fixed at bottom and always visible */}
        {/* BELANGRIJK: pointer-events-auto op Menu-indicator om het klikbaar te maken. */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.2
      }} className="mb-4 sm:mb-6 md:mb-8 pointer-events-auto z-20" data-id="element-760">
          <div className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl ${themeClasses.card} border ${themeClasses.border} backdrop-blur-xl flex items-center gap-2 sm:gap-3 shadow-lg`} data-id="element-761">
            <div className="flex items-center gap-2" data-id="element-762">
              <div className="w-2 h-2 rounded-full bg-purple-500 pulse-glow" data-id="element-763" />
              <span className={`text-xs sm:text-sm ${themeClasses.text} opacity-70 font-medium`} data-id="element-764">
                Gebruik het menu onderaan voor meer opties
              </span>
              <motion.div animate={{
              y: [0, 4, 0]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-765">
                <ChevronDownIcon className={`w-4 h-4 ${themeClasses.text} opacity-70`} data-id="element-766" />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}